import { SyntheticEvent } from "react"
import capitalize from "lodash/capitalize"

import { selectIsXS } from "@/lib/storage/app-slice"
import {
  selectAuthFlowEmail,
  selectAuthFlowEmailLocked,
  selectAuthFlowError,
  selectAuthFlowLoading,
  selectAuthFlowPassword,
  selectAuthFlowProviders,
  setAuthFlowCfToken,
  setAuthFlowEmail,
  setAuthFlowError,
  setAuthFlowPassword,
} from "@/lib/storage/auth-slice"
import {
  handleSignInStepSubmit,
  showEmailStep,
  showForgottenPasswordStep,
} from "@/lib/storage/auth-thunks"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import { shouldTurnstile, turnstileErrorAction } from "@/lib/turnstile"
import { isTouch } from "@/lib/utils/dom"
import { AuthProviderButtons } from "@/components/auth/auth-provider-buttons"
import { Turnstile } from "@/components/turnstile"
import { Button } from "../ui/button"
import { Form, FormControl, FormField } from "../ui/form"
import { PasswordInput } from "../ui/password-input"
import { TextInput } from "../ui/text-input"

export default function AuthStepSignIn() {
  const dispatch = useAppDispatch()
  const email = useAppSelector(selectAuthFlowEmail)
  const password = useAppSelector(selectAuthFlowPassword)
  const loading = useAppSelector(selectAuthFlowLoading)
  const error = useAppSelector(selectAuthFlowError)
  const emailLocked = useAppSelector(selectAuthFlowEmailLocked)
  const isXS = useAppSelector(selectIsXS)
  const doAutoFocus = !(isXS && isTouch())
  const providers = useAppSelector(selectAuthFlowProviders)

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Only validate form if password field is shown
    const formValid = showPasswordField
      ? e.currentTarget.reportValidity()
      : true
    void dispatch(handleSignInStepSubmit({ formValid }))
  }

  const handleCfTokenChange = (token: string) => {
    dispatch(setAuthFlowCfToken(token))
  }

  const handleTurnstileRetry = () => {
    dispatch(setAuthFlowCfToken(""))
  }

  const handleTurnstileError = (errorCode: string) => {
    if (turnstileErrorAction(errorCode) !== "terminal") {
      handleTurnstileRetry()
      return
    }

    handleTurnstileFailure()
  }

  const handleTurnstileFailure = () => {
    dispatch(setAuthFlowCfToken(""))
    dispatch(setAuthFlowError("Unable to sign in"))
  }

  const displayProviders = providers.filter((p) => p !== "email")
  const showPasswordField = providers.includes("email")

  return (
    <>
      {error && <p className="font-medium text-destructive">{error}</p>}

      <Form method="post" onSubmit={handleSubmit}>
        <FormField
          name="email"
          // click listener for "change" button is here so you can click anywhere on the input
          onClick={
            !emailLocked ? () => void dispatch(showEmailStep()) : undefined
          }
        >
          <FormControl
            render={
              <TextInput
                type="email"
                autoComplete="username"
                placeholder="Email"
                required
                size="lg"
                value={email}
                onChange={(e) => dispatch(setAuthFlowEmail(e.target.value))}
                readOnly
                disabled={loading}
                className={emailLocked ? undefined : "pr-20"}
              />
            }
          />
          {!emailLocked && (
            <button
              type="button"
              className="absolute top-0 right-1 flex h-(--input-lg-size) items-center px-2 text-2xs font-medium hover:underline"
            >
              Change
            </button>
          )}
        </FormField>

        {showPasswordField && (
          <FormField name="password">
            <PasswordInput
              control={FormControl}
              placeholder="Password"
              autoComplete="current-password"
              required
              size="lg"
              value={password}
              onChange={(e) => dispatch(setAuthFlowPassword(e.target.value))}
              disabled={loading}
              autoFocus={doAutoFocus}
            />
          </FormField>
        )}

        {shouldTurnstile("sign-in") && (
          <div className="flex justify-center">
            <Turnstile
              id="sign-in-turnstile"
              onSuccess={handleCfTokenChange}
              onError={handleTurnstileError}
              onExpire={handleTurnstileRetry}
              onUnsupported={handleTurnstileFailure}
              onScriptLoadError={handleTurnstileFailure}
            />
          </div>
        )}

        {showPasswordField && (
          <Button
            type="submit"
            variant="primary"
            block
            size="lg"
            loading={loading}
          >
            Continue
          </Button>
        )}
      </Form>
      {displayProviders.length > 0 ? (
        <>
          <p>
            This email is already registered with{" "}
            {displayProviders.map(capitalize).join(" and ")} sign in.
          </p>

          <AuthProviderButtons />
        </>
      ) : null}
      {showPasswordField && (
        // mt-auto pins it to the bottom of the auth flow's min height.
        <Button
          type="button"
          variant="link"
          size="lg"
          className="mx-auto mt-auto no-underline"
          onClick={() => void dispatch(showForgottenPasswordStep())}
        >
          Forgot password?
        </Button>
      )}
    </>
  )
}
